/**
 * -----------------------------------------------------------------------------
 * Popup - Create New User
 * -----------------------------------------------------------------------------
 * This popup is used for creating a new user in the organization.
 */

import { Formik, useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useCloneOrganizationRoleMutation } from "../../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import {
  setModalsData,
  TListingQueryParams,
  TModalsState,
} from "../../../api/store/commonSlice";
import {
  AdminFormFieldInput,
  AdminFormFieldSubmit,
} from "../../../components/admin/formFields";
import FTModal from "../../../components/common/FTModal";
import { routeUrls } from "../../../navigation/routeUrls";
import { useLoggedInUserPerms } from "../../../utils/user";
import {
  formInitialValues,
  roleDetailsSysadminYupValidationSchema,
  TFormFieldNames,
  YupValidationSchema,
} from "./validation";
import { FC, useMemo } from "react";
import FormActionButtons from "../../../components/admin/formActionButtons";
import { TLegacyUserRole } from "../../../api/types/UserRole";
import { APP_CONFIG } from "../../../constants/constants";

type AdminsRolesCloneToNewProps = { originalRole: TLegacyUserRole };

const AdminsRolesCloneToNew: FC<AdminsRolesCloneToNewProps> = ({
  originalRole,
}) => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.roles.create_new",
  });
  const { t: tCloneToNew } = useTranslation("translation", {
    keyPrefix: "admins.roles.clone_to_new",
  });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loggedInUserIsSysAdmin } = useLoggedInUserPerms();

  // user mutation
  const [cloneOrgRoleAPITrigger] = useCloneOrganizationRoleMutation();

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showCloneRole: false }));
  };

  const formik = useFormik({
    initialValues: formInitialValues,
    validationSchema:
      loggedInUserIsSysAdmin() === true
        ? roleDetailsSysadminYupValidationSchema
        : YupValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      // if(APP_CONFIG.TOASTS.INFO) toast.info(t('loginScreen.toast.logging_you_in'));
      cloneOrgRoleAPITrigger({
        org_role_account_id: originalRole.is_system_role
          ? APP_CONFIG.PRIMARY_SYSADMIN_ACCOUNT
          : `${listingQueryParams.users.accountid}`,
        original_role_id: originalRole.roleid,
        new_role_id: values.role_id,
        display_name: values.role_id,
        description: values.description,
      })
        .unwrap()
        .then((data) => {
          if (!!data?.roleid) {
            dispatch(setModalsData({ ...modalsState, showCloneRole: false }));
            toast.success(t("toast.create_success"), { autoClose: 10000 });
            navigate(
              `${routeUrls.dashboardChildren.adminChildren.roles}/${data?.roleid}`,
              { state: { new: true }, replace: true }
            );
          } else {
            toast.error(t("toast.create_failed"));
          }
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.create_failed")];
          // if getting duplicate/Duplicate string in error message then show the message as "Role ID already exists"
          if (errors[0].includes("Duplicate")) {
            toast.error(t("toast.create_duplicate_failed"));
          } else {
            toast.error(errors?.join(" "));
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    dirty,
    setFieldValue,
  } = formik;

  const invalidFields = Object.keys(errors).filter(
    (key) => !!errors[key as TFormFieldNames]
  );
  const isFormValid = invalidFields.length === 0 && dirty;
  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              label: t("submit_button"),
              variant: "primary",
              disabled: !isFormValid || isSubmitting /* || isLoading*/,
            },
            {
              label: tMain("cancel"),
              type: "button",
              variant: "danger-transparent",
              disabled: isSubmitting /* || isLoading*/,
              onClick: () => hideModal(),
            },
          ]}
        />
      </>
    );
  }, [isFormValid, isSubmitting]);
  return (
    <FTModal
      visible={modalsState.showCloneRole}
      heading={tCloneToNew("heading", { original_role: originalRole.roleid })}
      onHide={hideModal}
    >
      {/* <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none"> */}
      <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
        <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
          {t("sub_heading")}
        </p>

        <AdminFormFieldInput
          label={t("role_id")}
          type="text"
          id="role_id"
          name="role_id"
          placeholder={t("role_id_placeholder")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.role_id}
          touched={touched.role_id}
          error={errors.role_id}
        />

        <AdminFormFieldInput
          label={t("description")}
          type="text"
          id="Description"
          name="description"
          placeholder={t("description_placeholder")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.description}
          touched={touched.description}
          error={errors.description}
        />

        {MemoizedFormActionButtons}
        {/* <AdminFormFieldSubmit
              label={t('submit_button')}
              variant="primary"
              disabled={!isFormValid || isSubmitting} // || isLoading
            />

            <AdminFormFieldSubmit
              label={tMain('cancel')}
              type="button"
              variant="danger-transparent"
              disabled={isSubmitting} // || isLoading
              onClick={hideModal}
            /> */}
      </form>
    </FTModal>
  );
};
export default AdminsRolesCloneToNew;
